import { useTranslation } from "react-i18next";
// src/pages/spending/SpendingDepositsList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiGet } from "../../api/client";
import { companyRoute } from "../../utils/routeHelpers";
import EmptyState from "../../components/EmptyState";
import { ListToolbar, SortOption, MenuItem } from "../../components/ListToolbar";
import { formatAnyDateToLocale } from "../../utils/dateUtils";
import { formatNumber, formatMoney } from "../../utils/formatNumber";
import { A, pillClass } from "../../styles/appleClasses";
import { Button } from "../../components/Button";

type DepositItem = {
  id: number;
  direction: "incoming" | "outgoing";
  party_name: string; // backend field (we display as Vendor)
  bank_name?: string | null;
  bank_account_id?: number | null;
  date: string; // YYYY-MM-DD (or possibly datetime)
  original_amount: number;
  remaining_amount: number;

  status: "open" | "partial" | "closed" | "voided";
  memo?: string | null;
};

type DepositDisplayStatus = "Open" | "Partial" | "Closed" | "Voided";

function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  const esc = (v: any) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv =
    [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n") + "\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatDate(input?: string | null) {
  return formatAnyDateToLocale(input);
}

function money(n: number | undefined | null) {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  return formatMoney(n);
}

// Derive display status: Partial when remaining is between 0 and original.
function getDepositDisplayStatus(d: DepositItem): DepositDisplayStatus {
  const eps = 0.000001;

  const apiStatus = String(d.status || "").trim().toLowerCase();
  if (apiStatus === "voided") return "Voided";
  if (apiStatus === "closed") return "Closed";
  if (apiStatus === "partial") return "Partial";

  const orig = Number(d.original_amount ?? 0);
  const rem = Number(d.remaining_amount ?? 0);

  if (rem <= eps) return "Closed";
  if (orig > eps && rem < orig - eps) return "Partial";
  return "Open";
}

export default function SpendingDepositsList() {
  const { t } = useTranslation();
  const displayStatus = (status: DepositDisplayStatus) => ({
    Open: t("spendingDeposits.statusOpen"),
    Partial: t("spendingDeposits.statusPartial"),
    Closed: t("spendingDeposits.statusClosed"),
    Voided: t("sales.list.voided"),
  })[status];
  const { company_id = "" } = useParams<{ company_id: string }>();
  const navigate = useNavigate();
  const go = (path: string) => navigate(companyRoute(company_id, path));
  const openDeposit = (id: number) => go(`/deposits/${id}`);

  const getC = async <T,>(path: string): Promise<T> => {
    const sep = path.includes("?") ? "&" : "?";
    return apiGet<T>(`${path}${sep}company_id=${company_id}`);
  };

  const collator = useMemo(
    () => new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }),
    []
  );

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortValue, setSortValue] = useState("date_desc");

  const [items, setItems] = useState<DepositItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | { local: true } | null>(null);

  const [page, setPage] = useState(1);

  const sortOptions: SortOption[] = [
    { label: t("sales.list.dateNewest"), value: "date_desc" },
    { label: t("sales.list.dateOldest"), value: "date_asc" },
    { label: t("directory.vendors.sortAsc"), value: "party_asc" },
    { label: t("directory.vendors.sortDesc"), value: "party_desc" },
    { label: t("spendingDeposits.remainingHigh"), value: "remaining_desc" },
    { label: t("spendingDeposits.remainingLow"), value: "remaining_asc" },
  ];

  const newItems: MenuItem[] = [{ label: t("spendingDeposits.new"), onClick: () => go("/deposits/new") }];

  useEffect(() => {
    if (!company_id) return;
    setLoading(true);
    setErr(null);

    getC<DepositItem[]>(`/deposits`)
      .then((d) => setItems(d || []))
      .catch((e: any) => setErr(e?.message || { local: true }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id]);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    let list = [...(items || [])];

    list = list.filter((it) => {
      const d = (it.date || "").slice(0, 10);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      if (!q) return true;

      const displayStatus = getDepositDisplayStatus(it).toLowerCase();
      const disp = formatDate(it.date).toLowerCase(); // allow searching MM/DD/YYYY too

      return (
        (it.party_name || "").toLowerCase().includes(q) ||
        (it.memo || "").toLowerCase().includes(q) ||
        String(it.original_amount ?? "").includes(q) ||
        String(it.remaining_amount ?? "").includes(q) ||
        (it.status || "").toLowerCase().includes(q) ||
        displayStatus.includes(q) ||
        d.includes(q) || // YYYY-MM-DD
        disp.includes(q) // MM/DD/YYYY
      );
    });

    const cmpText = (a: string, b: string) => collator.compare(a || "", b || "");
    const cmpNum = (a: number, b: number) => (a || 0) - (b || 0);

    list.sort((a, b) => {
      switch (sortValue) {
        case "date_asc":
          return (a.date || "").localeCompare(b.date || "");
        case "party_asc":
          return cmpText(a.party_name || "", b.party_name || "");
        case "party_desc":
          return -cmpText(a.party_name || "", b.party_name || "");
        case "remaining_asc":
          return cmpNum(a.remaining_amount, b.remaining_amount);
        case "remaining_desc":
          return -cmpNum(a.remaining_amount, b.remaining_amount);
        case "date_desc":
        default:
          return (b.date || "").localeCompare(a.date || "");
      }
    });

    return list;
  }, [items, search, fromDate, toDate, sortValue, collator]);

  // reset paging on filter/sort/data changes
  useEffect(() => {
    setPage(1);
  }, [search, fromDate, toDate, sortValue, items.length]);

  const ROWS_PER_PAGE = 10;
  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));
  const fromRow = totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const toRow = totalFiltered === 0 ? 0 : Math.min(page * ROWS_PER_PAGE, totalFiltered);

  // clamp page if data shrinks
  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE),
    [filtered, page]
  );

  const moreItems: MenuItem[] = [
    {
      label: t("directory.common.exportCsv"),
      onClick: () => {
        const headers = ["Date", "Vendor", "Original", "Remaining", "Status", "Memo", "ID"];
        const rows = filtered.map((d) => [
          formatDate(d.date),
          d.party_name || "",
          d.original_amount ?? "",
          d.remaining_amount ?? "",
          getDepositDisplayStatus(d),
          d.memo || "",
          d.id,
        ]);
        downloadCsv("deposits.csv", headers, rows);
      },
    },
  ];

  // Header sort arrows (Expenses-style)
  const sortKey = sortValue.replace(/_(asc|desc)$/, "");
  const sortDir = sortValue.endsWith("_asc") ? "asc" : "desc";
  const handleHeaderSort = (key: "date" | "party" | "remaining") => {
    setSortValue((prev) => {
      const prevKey = prev.replace(/_(asc|desc)$/, "");
      const prevDir = prev.endsWith("_asc") ? "asc" : "desc";
      if (prevKey === key) return `${key}_${prevDir === "asc" ? "desc" : "asc"}`;
      return `${key}_desc`;
    });
  };

  const pagerText = (
    <span className={`${A.label} text-appleblue select-none`}>
      {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
    </span>
  );

  // For tiny icon-like paging buttons, keep it minimal and stable
  const pagerBtnCls = "px-2 active:scale-100";

  return (
    <div className={`w-full ${A.body}`}>
      <ListToolbar
        maxWidthClass="max-w-6xl"
        search={search}
        onSearchChange={setSearch}
        sortValue={sortValue}
        sortOptions={sortOptions}
        onSortChange={setSortValue}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        newItems={newItems}
        moreItems={moreItems}
        searchPlaceholder={t("spendingDeposits.search")}
        moreLabel={t("directory.common.more")}
        sortLabel={t("directory.common.sort")}
        fromLabel={t("sales.shared.from")}
        toLabel={t("sales.shared.to")}
        openCalendarLabel={t("sales.shared.openCalendar")}
      />

      {loading ? (
        <div className={`text-center py-6 ${A.body} text-gray-500`}>{t("spendingDeposits.loading")}</div>
      ) : err ? (
        <EmptyState
          message={typeof err === "string" ? err : t("spendingDeposits.loadError")}
          heading={t("directory.common.nothingHere")}
          backLabel={t("directory.common.goBack")}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          message={t("spendingDeposits.empty")}
          heading={t("directory.common.nothingHere")}
          backLabel={t("directory.common.goBack")}
          showNewButton
          newButtonText={t("spendingDeposits.newPlus")}
          newButtonPath={companyRoute(company_id, "/deposits/new")}
        />
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3 w-full max-w-6xl px-2 mt-3">
            {paged.map((d) => {
              const label = getDepositDisplayStatus(d);
              const tone =
                label === "Closed"
                  ? "success"
                  : label === "Partial"
                    ? "warning"
                    : label === "Voided"
                      ? "neutral"
                      : "brand";

              return (
                <div
                  key={d.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openDeposit(d.id)}
                  onKeyDown={(e) => (e.key === "Enter" ? openDeposit(d.id) : null)}
                  className={[
                    A.card,
                    "p-4 cursor-pointer transition",
                    "hover:bg-gray-50 active:ring-2 active:ring-appleblue/20",
                    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                  ].join(" ")}
                  style={{ outline: "none" }}
                  aria-label={t("expenses.list.viewDeposit", { id: d.id })}
                >
                  <div className="flex items-center justify-between">
                    <div className={`${A.title} tabular-nums`}>{formatDate(d.date)}</div>
                    <span className={pillClass(tone as any)}>{displayStatus(label)}</span>
                  </div>

                  <div className="mt-2 font-medium text-gray-700 truncate">
                    {d.party_name || "—"}
                  </div>

                  <div className="mt-2 flex justify-between items-center">
                    <div className={A.label}>{t("spendingDeposits.remaining")}</div>
                    <div className={`${A.title} ${A.num}`}>{money(d.remaining_amount || 0)}</div>
                  </div>

                  {d.memo ? (
                    <div className={`${A.label} mt-2 text-gray-500 line-clamp-2`}>{d.memo}</div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Pagination (mobile) */}
          <div className="md:hidden flex justify-between items-center mt-4 w-full max-w-6xl px-2">
            {pagerText}
            <div className="flex gap-2">
              <Button
                variant="tertiary"
                minWidth="0px"
                className={pagerBtnCls}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                type="button"
              >
                {t("directory.common.prev")}
              </Button>
              <Button
                variant="tertiary"
                minWidth="0px"
                className={pagerBtnCls}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                type="button"
              >
                {t("directory.common.next")}
              </Button>
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block w-full max-w-6xl mx-auto px-2 mt-3">
            <div className={`${A.card} overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className={`w-full ${A.body}`}>
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th
                        className={`${A.label} py-3 px-3 text-left font-medium select-none cursor-pointer`}
                        onClick={() => handleHeaderSort("date")}
                        title={t("sales.list.sortDate")}
                      >
                        {t("sales.common.date")}{" "}
                        {sortKey === "date" && (
                          <span className="text-gray-400">{sortDir === "asc" ? "↑" : "↓"}</span>
                        )}
                      </th>

                      <th
                        className={`${A.label} py-3 px-3 text-left font-medium select-none cursor-pointer`}
                        onClick={() => handleHeaderSort("party")}
                        title={t("directory.vendors.sortVendor")}
                      >
                        {t("directory.vendors.singular")}{" "}
                        {sortKey === "party" && (
                          <span className="text-gray-400">{sortDir === "asc" ? "↑" : "↓"}</span>
                        )}
                      </th>

                      <th className={`${A.label} py-3 px-3 text-right font-medium`}>{t("spendingDeposits.original")}</th>

                      <th
                        className={`${A.label} py-3 px-3 text-right font-medium select-none cursor-pointer`}
                        onClick={() => handleHeaderSort("remaining")}
                        title={t("spendingDeposits.sortRemaining")}
                      >
                        {t("spendingDeposits.remaining")}{" "}
                        {sortKey === "remaining" && (
                          <span className="text-gray-400">{sortDir === "asc" ? "↑" : "↓"}</span>
                        )}
                      </th>

                      <th className={`${A.label} py-3 px-3 text-left font-medium`}>{t("sales.common.status")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paged.map((d) => {
                      const label = getDepositDisplayStatus(d);
                      const tone =
                        label === "Closed"
                          ? "success"
                          : label === "Partial"
                            ? "warning"
                            : label === "Voided"
                              ? "neutral"
                              : "brand";

                      return (
                        <tr
                          key={d.id}
                          className="border-t border-gray-50 hover:bg-gray-50 cursor-pointer transition"
                          onClick={() => openDeposit(d.id)}
                          tabIndex={0}
                          onKeyDown={(e) => (e.key === "Enter" ? openDeposit(d.id) : null)}
                          style={{ outline: "none" }}
                          aria-label={t("expenses.list.viewDeposit", { id: d.id })}
                        >
                          <td className={`py-3 px-3 tabular-nums whitespace-nowrap ${A.num}`}>
                            {formatDate(d.date)}
                          </td>

                          <td className="py-3 px-3 min-w-0">
                            <div className="min-w-0 font-medium truncate">{d.party_name || "—"}</div>
                            {d.memo ? <div className={`${A.label} mt-0.5 text-gray-400 truncate`}>{d.memo}</div> : null}
                          </td>

                          <td className={`py-3 px-3 text-right tabular-nums font-medium whitespace-nowrap ${A.num}`}>
                            {money(d.original_amount || 0)}
                          </td>

                          <td className={`py-3 px-3 text-right tabular-nums font-medium whitespace-nowrap ${A.num}`}>
                            {money(d.remaining_amount || 0)}
                          </td>

                          <td className="py-3 px-3">
                            <span className={pillClass(tone as any)}>{displayStatus(label)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination (desktop) */}
              <div className="flex justify-between items-center mt-2 px-3 pb-3">
                {pagerText}
                <div className="flex gap-2">
                  <Button
                    variant="tertiary"
                    minWidth="0px"
                    className={pagerBtnCls}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    type="button"
                  >
                    {t("directory.common.prev")}
                  </Button>
                  <Button
                    variant="tertiary"
                    minWidth="0px"
                    className={pagerBtnCls}
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    type="button"
                  >
                    {t("directory.common.next")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
